import { useState } from "react";
import { MessageSquare, Star } from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { formatDate } from "@/utils/format";
import {
  useReviewList,
  useReplyReview,
  type ReviewRow,
} from "@/hooks/useReviews";

interface Props {
  businessId: string;
}

interface ReplyFormProps {
  review: ReviewRow;
  onSaved: () => void;
}

const ReplyForm = ({ review, onSaved }: ReplyFormProps) => {
  const [text, setText] = useState(review.providerReply ?? "");
  const reply = useReplyReview();

  const handleSubmit = async () => {
    if (!text.trim()) return;
    try {
      await reply.mutateAsync({ id: review.id, replyText: text.trim() });
      toast.success("Reply saved");
      onSaved();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="mt-3 space-y-2">
      <textarea
        className="w-full rounded-btn border border-border bg-muted/30 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
        rows={3}
        maxLength={1000}
        placeholder="Write a reply..."
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {text.length}/1000
        </span>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={reply.isPending || !text.trim()}
          className="px-3 py-1.5 text-xs rounded-btn bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {reply.isPending ? "Saving…" : "Save Reply"}
        </button>
      </div>
    </div>
  );
};

const ReviewCard = ({ review }: { review: ReviewRow }) => {
  const [replying, setReplying] = useState(false);

  return (
    <div className="bg-card border border-border rounded-card p-4 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">
            {review.customer?.name ??
              review.customer?.user?.phone ??
              "Customer"}
          </span>
          <span className="inline-flex items-center gap-0.5 text-warning text-sm font-semibold">
            <Star className="w-3.5 h-3.5 fill-warning" />
            {review.ratingOverall}
          </span>
          <StatusBadge status={review.status} />
        </div>
        <span className="text-xs text-muted-foreground shrink-0">
          {formatDate(review.createdAt)}
        </span>
      </div>

      {review.reviewText && (
        <p className="text-sm text-foreground">{review.reviewText}</p>
      )}

      {review.providerReply && !replying && (
        <div className="border-l-2 border-primary/40 pl-3 py-0.5 bg-primary/5 rounded-r-btn">
          <p className="text-xs font-semibold text-primary/80 mb-0.5">
            Provider reply
          </p>
          <p className="text-sm text-foreground">{review.providerReply}</p>
        </div>
      )}

      {replying ? (
        <ReplyForm review={review} onSaved={() => setReplying(false)} />
      ) : (
        <button
          type="button"
          onClick={() => setReplying(true)}
          className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
        >
          <MessageSquare className="w-3.5 h-3.5" />
          {review.providerReply ? "Edit reply" : "Reply"}
        </button>
      )}
    </div>
  );
};

export const ReviewsTab = ({ businessId }: Props) => {
  const { data, isLoading, error } = useReviewList({ businessId, limit: 100 });
  const reviews = data?.data ?? [];

  if (isLoading) return <Skeleton className="h-60 w-full" />;
  if (error)
    return <p className="text-sm text-destructive">Failed to load reviews.</p>;

  return (
    <div className="space-y-3">
      {reviews.length === 0 ? (
        <div className="bg-card border border-border rounded-card p-8 text-center text-sm text-muted-foreground">
          No reviews yet for this business.
        </div>
      ) : (
        reviews.map((r) => <ReviewCard key={r.id} review={r} />)
      )}
    </div>
  );
};
